import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BadgeCheck, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/timefmt";

const fmtHist = (h) => {
  const d = h.at ? new Date(h.at) : null;
  const dt = d ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : "";
  return `${dt} | ${h.item} | ${h.old} → ${h.new} | ${h.user_name}`;
};

export default function TaskReviewDialog({ task, onClose, onChanged }) {
  const { user } = useAuth();
  const taskLabel = user?.terminology?.task_label || "Task";
  const [tab, setTab] = useState("details");
  const [viewed, setViewed] = useState(new Set(["details"]));
  const [cfg, setCfg] = useState({ modes: [], sequence_groups: [], sequences: [] });
  const [locRequired, setLocRequired] = useState(false);
  const [comments, setComments] = useState(task.auth_comments || "");
  const [restrictions, setRestrictions] = useState(task.auth_restrictions || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/task-config?company_id=${user.company_id}`).then((r) => setCfg(r.data)).catch(() => {});
    api.get(`/locations?company_id=${user.company_id}`).then((r) =>
      setLocRequired((r.data || []).some((l) => l.task_selectable && !l.common))).catch(() => {});
  }, [user?.company_id]);

  const modesAvail = (cfg.modes?.length || 0) + (cfg.sequence_groups?.length || 0) > 0;
  const complete = {
    details: !!(task.date && task.start && task.end),
    crew: true,
    locations: !locRequired || !!task.primary_location_id,
    modes: !modesAvail || ((task.mode_ids || []).length + (task.sequence_ids || []).length > 0),
    auth: !!task.auth_officer_id,
    changelog: (task.history || []).length > 0,
  };
  const tabs = [
    ["details", `${taskLabel} Details`],
    ["crew", "Crew"],
    ["locations", "Locations"],
    ["modes", "Modes"],
    ["auth", "Authorisation"],
    ["changelog", "Change Log"],
  ];
  const openTab = (id) => { setTab(id); setViewed((v) => new Set(v).add(id)); };
  const allViewed = tabs.every(([id]) => viewed.has(id));

  const modeName = (id) => cfg.modes.find((m) => m.id === id)?.name || id;
  const seqName = (id) => (cfg.sequences || []).find((s) => s.id === id)?.name
    || (cfg.sequence_groups || []).flatMap((g) => g.sequences || []).find((s) => s.id === id)?.name || id;

  const authorise = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/authorise`, { on: true, comments, restrictions });
      toast.success(`${taskLabel} authorised`);
      onChanged();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Row = ({ label, children }) => (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-slate-800/60 last:border-0">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-200 text-right">{children || "—"}</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-950 border-slate-800" data-testid="task-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Review — {task.name || task.task_type_name}
            <span className="text-xs font-mono-num text-slate-400 ml-2">{fmtDate(task.date)} · {task.start}–{task.end}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 mb-4 border-b border-slate-800 overflow-x-auto" data-testid="review-tabs">
          {tabs.map(([id, label]) => {
            const ok = complete[id] && viewed.has(id);
            const seenIncomplete = viewed.has(id) && !complete[id];
            return (
              <button key={id} onClick={() => openTab(id)} data-testid={`review-tab-${id}`}
                className={`px-3.5 py-2 text-sm font-medium -mb-px border-b-2 transition-colors whitespace-nowrap ${
                  ok ? "border-emerald-500 text-emerald-300"
                  : seenIncomplete ? "border-amber-500/70 text-amber-300"
                  : tab === id ? "border-blue-500 text-white"
                  : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                {label}
              </button>
            );
          })}
        </div>

        {tab === "details" && (
          <div data-testid="review-details">
            <Row label="Type">{task.task_type_name}</Row>
            <Row label="Name">{task.name}</Row>
            <Row label="Date">{fmtDate(task.date)}{task.end_date ? ` → ${fmtDate(task.end_date)}` : ""}</Row>
            <Row label="Time">{task.start}–{task.end} ({task.timezone || "UTC"})</Row>
            <Row label="Brief / Debrief">{task.brief_min || 0} min / {task.debrief_min || 0} min</Row>
            <Row label="Status"><span className="uppercase text-xs tracking-widest">{task.status}</span></Row>
            <Row label="Created by">{task.created_by_name}</Row>
            <Row label="Remarks">{task.remarks}</Row>
            <Row label="Comments">{task.comments}</Row>
            <Row label="Airspace">{task.airspace}</Row>
          </div>
        )}
        {tab === "crew" && (
          <div data-testid="review-crew">
            <Row label="Lead">{task.created_by_name}</Row>
            {(task.participants || []).length === 0 && <div className="text-sm text-slate-500 py-2">No additional crew.</div>}
            {(task.participants || []).map((p) => <Row key={p.user_id} label="Crew">{p.name}{p.role_name ? ` (${p.role_name})` : ""}</Row>)}
          </div>
        )}
        {tab === "locations" && (
          <div data-testid="review-locations">
            <Row label="Primary">{task.primary_location_name}</Row>
            {(task.additional_location_names || []).map((n, i) => <Row key={i} label="Additional">{n}</Row>)}
            <Row label="End location">{task.end_location_name}</Row>
            {!task.primary_location_id && locRequired && (
              <div className="text-xs text-amber-300 mt-2" data-testid="review-locations-missing">No primary location set.</div>
            )}
          </div>
        )}
        {tab === "modes" && (
          <div data-testid="review-modes">
            {(task.mode_ids || []).map((id) => <Row key={id} label="Mode">{modeName(id)}</Row>)}
            {(task.sequence_ids || []).map((id) => <Row key={id} label="Sequence">{seqName(id)}</Row>)}
            {(task.equipment_labels || []).map((l, i) => <Row key={i} label="Equipment">{l}</Row>)}
            <Row label="Risk / Complexity">{task.risk_total || 0} / {task.complexity_total || 0}</Row>
            {modesAvail && !(task.mode_ids || []).length && !(task.sequence_ids || []).length && (
              <div className="text-xs text-amber-300 mt-2" data-testid="review-modes-missing">No modes or sequences selected.</div>
            )}
          </div>
        )}
        {tab === "auth" && (
          <div className="space-y-3" data-testid="review-auth">
            <Row label="Auth Officer">{task.auth_officer_id ? "Assigned" : "Not assigned"}</Row>
            {task.auth_comments && <Row label="Comments">{task.auth_comments}</Row>}
            {task.auth_restrictions && <Row label="Restrictions">{task.auth_restrictions}</Row>}
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Authorisation comments (optional)</label>
              <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={2} data-testid="review-auth-comments"
                className="w-full px-3 py-2 rounded-md bg-slate-900 border border-slate-800 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Restrictions (optional)</label>
              <textarea value={restrictions} onChange={(e) => setRestrictions(e.target.value)} rows={2} data-testid="review-auth-restrictions"
                className="w-full px-3 py-2 rounded-md bg-slate-900 border border-slate-800 text-sm" />
            </div>
            <button onClick={authorise} disabled={!allViewed || busy} data-testid="review-authorise-btn"
              title={allViewed ? "Authorise this task" : "View every tab before authorising"}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-violet-600/20 hover:bg-violet-600/30 border border-violet-600/50 text-violet-200 text-sm font-semibold transition-colors disabled:opacity-40">
              <BadgeCheck className="h-4 w-4" /> {busy ? "Working…" : allViewed ? `Authorise ${taskLabel}` : `View all tabs to authorise (${viewed.size}/${tabs.length})`}
            </button>
          </div>
        )}
        {tab === "changelog" && (
          <div data-testid="review-changelog">
            {(task.history || []).length === 0 && <div className="text-sm text-slate-500">No changes logged.</div>}
            <div className="rounded-md border border-slate-800 bg-slate-950/60 divide-y divide-slate-800/60 max-h-72 overflow-y-auto">
              {(task.history || []).map((h, i) => (
                <div key={i} className="px-3 py-1.5 text-[11px] font-mono-num text-slate-300" data-testid={`review-changelog-row-${i}`}>{fmtHist(h)}</div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="review-progress">
          <Eye className="h-3 w-3" /> {viewed.size} of {tabs.length} tabs viewed
          {tabs.filter(([id]) => viewed.has(id) && !complete[id]).length > 0 && (
            <span className="text-amber-400">· amber tabs have missing information</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
